import moment from "moment";
import local from "../localization/strings";

/**
 * Reads the allowance the way the cloud sender does, so the meter never says one thing while the
 * site administrators' emails say another.
 *
 * The sender refuses a text only when nothing is left, and emails the site administrators once usage
 * crosses `warningPercentUsed`. The two "refused" states outrank the balance: a site with 400 segments
 * and its SMS switch off is sending nothing, and that is what the reader needs to know.
 *
 * Statuses:
 * - `unused`    — the site has no cloud SMS at all (no annual limit and no packages): show nothing.
 * - `off`       — the site's SMS switch is off: every text is refused whatever the balance.
 * - `exhausted` — nothing left: every text is refused until segments are added.
 * - `warning`   — past the percentage the administrators were emailed at.
 * - `normal`    — segments in hand.
 */
export const smsAllowanceView = (allowance) => {
	if (!allowance) return null;

	const total = Math.max(0, allowance.segmentsAdded);
	const remaining = Math.min(total, Math.max(0, allowance.segmentsRemaining));
	const percentUsed = total === 0 ? 0 : Math.round(((total - remaining) / total) * 100);
	// The bar is scaled to the annual allowance, not to everything the site happens to hold, which is
	// the scale SmsUsage already uses on the admin and distributor lists. A site given a one-off package
	// otherwise has a bar whose full width means something different this year from last, and a figure
	// read on one screen does not match the same figure read on the other. Extras simply fill it and it
	// clamps, exactly as SmsUsage clamps. A site with no limit falls back to what it holds, or there
	// would be nothing to scale against at all.
	const scale = Math.max(0, allowance.annualSmsSegmentLimit) || total;
	const percentLeft = scale === 0 ? 0 : Math.min(100, Math.round((remaining / scale) * 100));

	let status;
	if (allowance.annualSmsSegmentLimit === 0 && total === 0) {
		status = "unused";
	} else if (!allowance.smsAlarmNotificationsEnabled) {
		status = "off";
	} else if (remaining === 0) {
		status = "exhausted";
	} else if (percentUsed >= allowance.warningPercentUsed) {
		status = "warning";
	} else {
		status = "normal";
	}

	return {
		status,
		remaining,
		total,
		percentUsed,
		percentLeft,

		scale,
		warningPercentUsed: allowance.warningPercentUsed,
		renewsUtc: allowance.freeAllowanceRenewsUtc,
		annualLimit: allowance.annualSmsSegmentLimit,
		extra: Math.max(0, allowance.extraSegments || 0),
		extraExpiryUtc: allowance.extraSegmentsExpiryUtc,
		extraPackagesLive: allowance.extraPackagesLive || 0,
	};
};

// Words and colours for the allowance's status, shared by the header chip, its dropdown and the
// sent-log dialog, so the same state is never described two ways.

/** The one-line status: what the texts are doing right now. */
export const smsAllowanceHeadline = (view) => {
	switch (view.status) {
		case "off":
			return local.TF_SmsAllowanceOff;
		case "exhausted":
			return local.TF_SmsAllowanceExhausted;
		case "warning":
			return local.TF_SmsAllowanceWarning;
		default:
			return local.TF_SmsAllowanceNormal;
	}
};

/** The sentence under the headline, for states that need one. Null when the headline says it all. */
export const smsAllowanceDetail = (view) => {
	switch (view.status) {
		case "off":
			return local.TF_SmsAllowanceOffDetail;
		case "exhausted":
			return local.TF_SmsAllowanceExhaustedDetail;
		case "warning":
			return local.formatString(local.TF_SmsAllowanceWarningDetail, view.warningPercentUsed);
		default:
			return null;
	}
};

/**
 * "312 segments left".
 *
 * No denominator. The only honest one is everything the site currently holds, which blends an allowance
 * that returns every year with packages that do not — so the figure moves between years for reasons
 * nothing on screen explains, and sits next to an annual allowance it does not contain. The bar gives
 * the sense of scale, and the lines beneath say what recurs and what runs out.
 */
export const smsAllowanceRemainingText = (view) => local.formatString(local.TF_SmsAllowanceRemaining, view.remaining.toLocaleString());

/**
 * "Your annual allowance of 400 renews on 15 Apr 2027", or null when the date is not known.
 *
 * Names the annual limit rather than the balance, because they are rarely the same number and only the
 * limit is the one that comes back. A site holding a one-off package sees a total that will not repeat,
 * and nothing else on this panel says which part of it recurs.
 */
export const smsAllowanceRenewsText = (view) => (view.renewsUtc ? local.formatString(local.TF_SmsAllowanceRenews, (view.annualLimit || 0).toLocaleString(), moment.utc(view.renewsUtc).format("DD MMM YYYY")) : null);

/**
 * "Includes 401 segments from additional packages, expiring 10 Nov 2026" — null unless the site holds
 * any. These are segments bought or given by hand: they run out on their own date and do not return,
 * which is the difference the annual line above cannot express on its own.
 *
 * The date is when they run out, so it is the last package to expire and it ignores any already spent.
 * With more than one still live it is the last of several, and says so: "expiring 10 Nov 2026" over two
 * packages would have the earlier one ending on a date it does not. With none left there is no date to
 * give, only the count.
 */
export const smsAllowanceExtraText = (view) => {
	if (!view.extra) return null;
	const total = view.extra.toLocaleString();
	if (!view.extraExpiryUtc) return local.formatString(local.TF_SmsAllowanceExtra, total);
	const expiry = moment.utc(view.extraExpiryUtc).format("DD MMM YYYY");
	return local.formatString(view.extraPackagesLive > 1 ? local.TF_SmsAllowanceExtraExpiringLast : local.TF_SmsAllowanceExtraExpiring, total, expiry);
};

/** Bootstrap text colour class for the status word. */
export const smsAllowanceTextClass = (status) => {
	switch (status) {
		case "off":
			return "text-secondary";
		case "exhausted":
			return "text-danger";
		case "warning":
			return "text-warning";
		default:
			return "text-success";
	}
};

/** Bootstrap background class for the meter's fill. */
export const smsAllowanceBarClass = (status) => {
	switch (status) {
		case "off":
			return "bg-secondary";
		case "exhausted":
			return "bg-danger";
		case "warning":
			return "bg-warning";
		default:
			return "bg-success";
	}
};
